/* eslint-disable react/jsx-one-expression-per-line */
import React, {FC, useCallback, useEffect, useRef, useState} from "react"
import {Variant} from "@mui/material/styles/createTypography"
import {formatTextTestIds} from "@next/utils"
import {Typography} from "@mui/material"
import connect from "./connect"
import {
    LoadingSpinner,
    SpinnerWrapper,
    HiddenNoTranslationFacetValue,
    FilterThumbnail,
    FilterThumbnailPlaceholder,
} from "./components"
import {StyledFacetButton, TextWrapper, Tick, TickLine1, TickLine2} from "../components"
import {FacetState} from "../../../models/FacetsState"
import {DEFAULT_FILTER_SPINNER_TIMEOUT, SCORE, SORT, THUMBNAIL_IMAGE_FALLBACK_PATH} from "../../../config/constants"
import noTranslationText from "../../../utils/noTranslationText"
import ShowFiltersText from "../showFiltersText"
import TabbedFilterTitleTypography from "./tabbedFilterTitleTypography"
import env from "../../../config/env"

interface Props {
    filterState: FacetState
    onFilterSelect: ({
        isTabbedFilter,
        filterName,
        facetName,
    }: {
        isTabbedFilter?: boolean
        filterName: string
        facetName?: string
    }) => void
    isTabbedFilter?: boolean
    tabAutoFocus?: boolean
    isFacetNameTextClipped?: boolean
    facetName: string
    enableHyperlinksForCategory?: boolean
    siteUrl: string
    imagePath?: string
}

export const TabbedFilterRenderer = (props: Props) => {
    if (!props.filterState) {
        return null
    }

    return <TabbedFilter {...props} />
}

export const TabbedFilter: FC<Props> = ({
    facetName,
    filterState: {n, c, s, v, d, u},
    onFilterSelect,
    isTabbedFilter,
    tabAutoFocus,
    isFacetNameTextClipped,
    imagePath,
}: Props) => {
    const isUnMountedRef = useRef(false)
    const [showLoading, setShowLoading] = useState(false)
    const variant: Variant = s ? "h5" : "body1"
    // don't animate ticks on page load
    const animate = useRef(false)
    const previousSelected = useRef(s)

    const classNames = showLoading ? "loading" : "loaded"

    useEffect(() => {
        if (showLoading) {
            if (s !== previousSelected.current) {
                setShowLoading(false)
            }

            setTimeout(() => {
                if (isUnMountedRef.current) return
                setShowLoading(false)
            }, DEFAULT_FILTER_SPINNER_TIMEOUT)
        }

        previousSelected.current = s
        return () => {
            isUnMountedRef.current = true
        }
    }, [showLoading, s])

    const isCheckedDefaultSort = facetName === SORT && v === SCORE && s
    const [imageLoaded, setImageLoaded] = useState<boolean>(false)

    const onClick = useCallback(() => {
        if (!isCheckedDefaultSort) {
            setShowLoading(true)
            animate.current = true
            onFilterSelect({isTabbedFilter, filterName: v, facetName})
        }
    }, [isCheckedDefaultSort, onFilterSelect, isTabbedFilter, v, facetName])

    const imageOnErrorHandler = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.onerror = null
        e.currentTarget.src = `${env.REACT_APP_CDN_BASEURL}${THUMBNAIL_IMAGE_FALLBACK_PATH}`
    }

    return (
        <StyledFacetButton
            onClick={onClick}
            data-testid={`plp-tabbed-${isTabbedFilter ? "facet" : "key-filter"}-button-${v}`}
            disabled={d}
            autoFocus={tabAutoFocus}
        >
            <TextWrapper noBorder isKeyFilter={!isTabbedFilter}>
                {
                    // eslint-disable-next-line react/no-danger
                    <HiddenNoTranslationFacetValue
                        data-testid={formatTextTestIds(`plp-facet-modal-hidden-label-${v}`)}
                        id={formatTextTestIds(`plp-facet-modal-hidden-label-${v}`)}
                        dangerouslySetInnerHTML={noTranslationText(n)}
                    />
                }
                <ShowFiltersText
                    variant={variant}
                    disabled={d}
                    component="p"
                    $isclipped={isFacetNameTextClipped}
                    row={isTabbedFilter}
                    childtestid={formatTextTestIds(`plp-tabbed-filter-products-${n}`)}
                >
                    <>
                        {imagePath && (
                            <>
                                <FilterThumbnail
                                    src={imagePath}
                                    alt={`${v}-thumbnail`}
                                    data-testid={`plp-tabbed-filter-search-category-thumbnail-${facetName}-${v}`}
                                    onLoad={() => setImageLoaded(true)}
                                    imageLoaded={imageLoaded}
                                    onError={imageOnErrorHandler}
                                />
                                <FilterThumbnailPlaceholder key={v} variant="circular" imageLoaded={imageLoaded} />
                            </>
                        )}
                        <TabbedFilterTitleTypography>
                            <>
                                <Typography
                                    component="span"
                                    color="text.primary"
                                    className={`${!isTabbedFilter ? "is-single-line" : ""}`}
                                >
                                    {n}
                                </Typography>
                                <Typography variant="body2" component="span" aria-label={`${c} Products`}>
                                    {c && `(${c})`}
                                </Typography>
                            </>
                        </TabbedFilterTitleTypography>
                    </>
                </ShowFiltersText>
                <SpinnerWrapper>
                    <LoadingSpinner className={classNames} data-testid="plp-tabbed-filter-spinner">
                        {s && !showLoading && (
                            <Tick data-testid="plp-tabbed-filter-tick">
                                <TickLine1 animate={animate.current} />
                                <TickLine2 animate={animate.current} />
                            </Tick>
                        )}
                    </LoadingSpinner>
                </SpinnerWrapper>
            </TextWrapper>
        </StyledFacetButton>
    )
}

export default connect(TabbedFilterRenderer)
